// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { AlertTriangle, FolderSync, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  partitionRunsForStore,
  type SftpManualRun,
  SftpManualRunError,
  useCreateSftpManualRunMutation,
  useSftpLocationsQuery,
  useSftpManualRunsQuery,
} from "@/api/hooks/use-sftp-manual-runs";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { DatePicker } from "@/components/shared/date-picker";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { KeyChip } from "@/components/shared/key-chip";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { ManualSftpRunList } from "@/features/imports/manual-sftp-run-list";

/** Matches the backend's `reason` column. */
const REASON_MAX_LENGTH = 500;

/** How many runs the in-dialog list pulls. Manual replays are rare; this is a generous ceiling. */
const RUNS_LIMIT = 50;

/** While the dialog is open, keep the run list fresh — statuses move on the cronjob's clock. */
const RUNS_POLL_MS = 30_000;

export interface ManualSftpImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Store surface. The replay is fixed to this store and locations are limited to its chain — a
   * store page is no place to queue something that reaches the rest of the chain.
   */
  store?: { storeId: number; storeName: string; chainName: string };
  /** Chain surface. Limits the location list, and scopes the optional store picker to the chain. */
  chainName?: string;
  canEdit: boolean;
}

/**
 * Queues a manual SFTP importer replay and shows what is already queued for the same scope.
 *
 * Worth knowing before reading on: submitting does **not** run an import. It files a `pending` row
 * that the location's next scheduled cronjob tick picks up, reading the requested day instead of
 * today. The wording throughout says "queued", never "importing", because the gap is up to an hour.
 */
export function ManualSftpImportDialog({ open, onOpenChange, store, chainName, canEdit }: ManualSftpImportDialogProps) {
  const { t } = useTranslation("back-office");
  const createRun = useCreateSftpManualRunMutation();

  const [locationName, setLocationName] = useState("");
  const [importDate, setImportDate] = useState<string | undefined>(undefined);
  const [storeId, setStoreId] = useState<number | undefined>(undefined);
  const [reason, setReason] = useState("");

  // The scope the dialog was opened in, which fixes the chain for everything below.
  const scopeChainName = store?.chainName ?? chainName;

  const { data: locationsData, isLoading: locationsLoading } = useSftpLocationsQuery({
    chainName: scopeChainName,
    enabled: open,
  });
  const locations = useMemo(() => locationsData?.locations ?? [], [locationsData?.locations]);
  const selectedLocation = locations.find((location) => location.locationName === locationName);

  // On the ingress page there is no scope, so the chosen location is what names the chain — both
  // for the store picker below and for the list of what is already queued.
  const effectiveChainName = scopeChainName ?? selectedLocation?.chainName;

  const { data: storesData, isLoading: storesLoading } = useStoresQuery({
    chainName: effectiveChainName,
    limit: -1,
    onlyActive: true,
    enabled: open && !store && !!effectiveChainName,
  });

  const storeOptions = useMemo(
    () =>
      (storesData?.stores ?? []).map((s) => ({
        label: s.legibleName,
        value: String(s.storeId),
      })),
    [storesData?.stores],
  );

  const {
    data: runsData,
    isLoading: runsLoading,
    isFetching: runsFetching,
  } = useSftpManualRunsQuery({
    // Scoped by the page, not by the feed currently selected in the form above — on the ingress
    // page that means every outstanding replay, which is the point of opening it from there.
    chainName: scopeChainName,
    limit: RUNS_LIMIT,
    enabled: open,
    refetchInterval: open ? RUNS_POLL_MS : false,
  });

  const resetMutation = createRun.reset;

  useEffect(
    function resetWhenClosed() {
      if (open) return;
      setLocationName("");
      setImportDate(undefined);
      setStoreId(undefined);
      setReason("");
      resetMutation();
    },
    [open, resetMutation],
  );

  // A store page always narrows to its own store; there is no widening control by design.
  const targetStoreId = store?.storeId ?? storeId;
  const isChainWide = targetStoreId == null;

  // On a store page, a sibling store's replay says nothing about this one — drop it. What stays is
  // this store's own rows plus the chain-wide ones, which cover it whether or not they name it.
  const relevantRuns = useMemo(() => {
    const runs = runsData?.manualRuns ?? [];
    if (!store) return runs;
    const { storeScoped, chainWide } = partitionRunsForStore(runs, store.storeId);
    return [...storeScoped, ...chainWide];
  }, [runsData?.manualRuns, store]);

  const canSubmit =
    canEdit && !!locationName && !!importDate && !createRun.isPending && !!selectedLocation?.isReplayable;

  async function onSubmit() {
    if (!locationName || !importDate) return;
    try {
      await createRun.mutateAsync({
        locationName,
        importDate,
        storeId: targetStoreId,
        reason: reason.trim() || undefined,
      });
      toast.success(t("imports.manualSftp.success", { location: locationName, date: importDate }));
      // Deliberately left open: the operator usually files several days in a row, and the list
      // below is the only place the new row shows up.
      setImportDate(undefined);
      setReason("");
    } catch {
      // Rendered inline — the failures are specific enough to be worth explaining in place.
    }
  }

  const errorCode = createRun.error instanceof SftpManualRunError ? createRun.error.code : undefined;

  // `storeCount` is a ceiling — the chain's active stores — not a prediction of how many stores
  // that day's drop actually carries files for, which only the SFTP server knows. Hence "up to".
  // It is null when the chain is unknown or has no active stores; that is "we can't tell you",
  // not "zero", so fall back to the countless wording rather than rendering a 0.
  const storeCount = selectedLocation?.storeCount;
  const chainWideWarningKey =
    storeCount == null ? "imports.manualSftp.warnChainWide" : "imports.manualSftp.warnChainWideCount";
  const chainWideWarningValues = { location: locationName, count: storeCount ?? 0 };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-manual-sftp-import"
        className="sm:max-w-2xl p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <div className="flex flex-row">
          <WizardPane icon={FolderSync} />
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <DialogHeader className="p-6 pb-0">
              <DialogTitle>{t("imports.manualSftp.title")}</DialogTitle>
              <p className="text-sm text-muted-foreground pt-1">{t("imports.manualSftp.description")}</p>
              {(store || chainName) && (
                <div className="flex items-center gap-2 pt-2">
                  {store && <KeyChip label={store.storeName} variant="store" />}
                  {scopeChainName && <KeyChip label={scopeChainName} variant="chain" />}
                </div>
              )}
            </DialogHeader>

            <div className="flex flex-col gap-4 overflow-y-auto max-h-[60vh] p-6 pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="manual-sftp-location">{t("imports.manualSftp.locationLabel")}</Label>
                <Select value={locationName} onValueChange={setLocationName} disabled={createRun.isPending}>
                  <SelectTrigger id="manual-sftp-location" className="w-full">
                    <SelectValue
                      placeholder={
                        locationsLoading
                          ? t("imports.manualSftp.locationsLoading")
                          : t("imports.manualSftp.locationPlaceholder")
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      // Non-replayable feeds stay listed rather than hidden: an operator who knows
                      // the feed exists and can't find it will assume the picker is broken.
                      <SelectItem
                        key={location.locationName}
                        value={location.locationName}
                        disabled={!location.isReplayable}
                      >
                        <span className="font-mono text-xs">{location.locationName}</span>
                        {!location.isReplayable && (
                          <span className="text-muted-foreground"> — {t("imports.manualSftp.notReplayable")}</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {locations.length === 0 && !locationsLoading && (
                  <p className="text-sm text-muted-foreground">
                    {t("imports.manualSftp.noLocations", { chain: scopeChainName })}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>{t("imports.manualSftp.dateLabel")}</Label>
                {/* Future days are refused by the backend — nothing has been delivered yet, so
                    there is nothing to replay. Grey them out rather than round-trip a 422. */}
                <DatePicker
                  value={importDate}
                  onChange={setImportDate}
                  placeholder={t("imports.manualSftp.datePlaceholder")}
                  disableAfter={new Date()}
                  fromYear={new Date().getFullYear() - 2}
                  toYear={new Date().getFullYear()}
                />
                <p className="text-xs text-muted-foreground">{t("imports.manualSftp.dateHint")}</p>
              </div>

              <div className="space-y-1.5">
                <Label>{t("imports.manualSftp.storeLabel")}</Label>
                {store ? (
                  <p className="text-sm text-muted-foreground">
                    {t("imports.manualSftp.storeFixed", { store: store.storeName })}
                  </p>
                ) : (
                  <VirtualSelectPopover
                    value={storeId == null ? "" : String(storeId)}
                    onChange={(value) => setStoreId(value ? Number(value) : undefined)}
                    options={storeOptions}
                    placeholder={t("imports.manualSftp.storePlaceholderChainWide")}
                    searchPlaceholder={t("imports.manualSftp.storeSearch")}
                    emptyText={t("imports.manualSftp.storeEmpty")}
                    loading={storesLoading}
                    disabled={createRun.isPending || !effectiveChainName}
                    clearable
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="manual-sftp-reason">{t("imports.manualSftp.reasonLabel")}</Label>
                <Textarea
                  id="manual-sftp-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value.slice(0, REASON_MAX_LENGTH))}
                  placeholder={t("imports.manualSftp.reasonPlaceholder")}
                  rows={2}
                  disabled={createRun.isPending}
                />
              </div>

              {/* Only chain-wide gets a warning. A store-scoped replay also overrides that store's
                  import settings, but the operator named the store, so consenting to it is the
                  point of the field — a caution there is noise that dilutes the one that matters. */}
              {locationName && selectedLocation?.isReplayable && isChainWide && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <p className="text-sm text-muted-foreground">{t(chainWideWarningKey, chainWideWarningValues)}</p>
                  </div>
                </div>
              )}

              {errorCode && (
                <div className="rounded-lg border border-destructive bg-destructive/10 p-3">
                  <p className="text-sm text-destructive">{t(`imports.manualSftp.errors.${errorCode}`)}</p>
                  {createRun.error instanceof SftpManualRunError && createRun.error.detail && (
                    <p className="pt-1 font-mono text-xs text-muted-foreground">{createRun.error.detail}</p>
                  )}
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium">{t("imports.manualSftp.queuedTitle")}</h3>
                  {runsFetching && !runsLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                </div>
                {runsLoading ? (
                  <InlineSpinner label={t("imports.manualSftp.queuedLoading")} />
                ) : (
                  <ManualSftpRunList runs={relevantRuns} scopedStoreId={store?.storeId} />
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={createRun.isPending}>
            {t("buttons.close")}
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {createRun.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderSync className="h-4 w-4" />}
            {t("imports.manualSftp.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type { SftpManualRun };
